// ===== Interfaces =====

import {
    IEditorAudioNoteItem,
}                                   from '../../../interfaces';

// ===== Enums =====

import {
    AUDIO_TRANSCRIPTION_JOB_STATUS,
}                                   from '../../../enums';

const AUDIO_NOTE_TRANSCRIPTION_STALLED_MS = 60 * 60 * 1000;
const AUDIO_NOTE_EXPECTED_ALIGNMENT_PROGRESS_BANDS = Object.freeze({
    canonical_candidate_chunk: Object.freeze({
        start: 35,
        end: 42,
    }),
    canonical_chunk: Object.freeze({
        start: 42,
        end: 44,
    }),
    weak_chunk: Object.freeze({
        start: 44,
        end: 98,
    }),
    rescue_window: Object.freeze({
        start: 98,
        end: 99,
    }),
});

/**
 * The worker already persists real phase-local counters for the long
 * expected-transcript tail. These ranges are based on successful Cloud Run
 * runs, where weak-chunk rescue dominates the tail by a wide margin, so the
 * client can reflect the part that actually consumes most of the runtime.
 * Keep these ranges aligned with the server-side expected-reference bands.
 */
const getAudioNoteExpectedAlignmentPhaseBand = (
    phaseUnitLabel: string | null | undefined,
): { start: number, end: number } | null => {
    if (typeof phaseUnitLabel !== 'string') {
        return null;
    }

    return AUDIO_NOTE_EXPECTED_ALIGNMENT_PROGRESS_BANDS[
        phaseUnitLabel as keyof typeof AUDIO_NOTE_EXPECTED_ALIGNMENT_PROGRESS_BANDS
    ] || null;
};

/**
 * Prefer the backend's overall phase percent when present. If it is missing,
 * fall back to completed/total unit counts so the UI can still show real
 * movement for older heartbeat payloads.
 */
const getAudioNoteExpectedAlignmentPhasePercent = (
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
): number | null => {
    if (!transcriptionJob) {
        return null;
    }

    if (
        typeof transcriptionJob.phaseProgressPercent === 'number'
        && Number.isFinite(transcriptionJob.phaseProgressPercent)
    ) {
        return Math.max(0, Math.min(100, Math.round(transcriptionJob.phaseProgressPercent)));
    }

    const {
        completedPhaseUnitCount,
        totalPhaseUnitCount,
    } = transcriptionJob;
    if (
        typeof completedPhaseUnitCount !== 'number'
        || !Number.isFinite(completedPhaseUnitCount)
        || typeof totalPhaseUnitCount !== 'number'
        || !Number.isFinite(totalPhaseUnitCount)
        || totalPhaseUnitCount <= 0
    ) {
        return null;
    }

    return Math.max(
        0,
        Math.min(100, Math.round((completedPhaseUnitCount / totalPhaseUnitCount) * 100)),
    );
};

/**
 * Maps the real expected-alignment phase-local percent into a wider display
 * band so the badge reflects genuine progress through the long tail stages.
 */
const getAudioNoteExpectedAlignmentDisplayProgress = (
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
): number | null => {
    const phaseBand = getAudioNoteExpectedAlignmentPhaseBand(
        transcriptionJob?.phaseUnitLabel,
    );
    const phaseProgressPercent = getAudioNoteExpectedAlignmentPhasePercent(
        transcriptionJob,
    );
    if (!phaseBand || phaseProgressPercent == null) {
        return null;
    }

    return Math.min(
        phaseBand.end,
        phaseBand.start + Math.round(
            ((phaseBand.end - phaseBand.start) * phaseProgressPercent) / 100,
        ),
    );
};

/**
 * The status badge is only useful while work is still in flight or when the
 * job failed. Completed jobs should disappear once the note is ready.
 */
export const shouldShowAudioNoteTranscriptionStatus = ({
    transcriptionJob,
}: {
    transcript: IEditorAudioNoteItem['transcript'] | null | undefined,
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
}): boolean => (
    isAudioNoteTranscriptionStatusPending(transcriptionJob)
    || isAudioNoteTranscriptionStatusStalled(transcriptionJob)
    || isAudioNoteTranscriptionStatusFailure(transcriptionJob)
);

/**
 * Jobs can stay queued/offloaded forever if a downstream worker stalls. Once a
 * pending job stops updating for long enough, stop showing it as actively
 * progressing so the UI does not imply work is still moving.
 */
export const isAudioNoteTranscriptionStatusStalled = (
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
    now = Date.now(),
): boolean => {
    if (!transcriptionJob) {
        return false;
    }

    const isPendingStatus = (
        transcriptionJob.status === AUDIO_TRANSCRIPTION_JOB_STATUS.queued
        || transcriptionJob.status === AUDIO_TRANSCRIPTION_JOB_STATUS.processing
        || transcriptionJob.status === AUDIO_TRANSCRIPTION_JOB_STATUS.offloaded
    );
    if (!isPendingStatus) {
        return false;
    }

    if (
        typeof transcriptionJob.updated !== 'number'
        || !Number.isFinite(transcriptionJob.updated)
    ) {
        return false;
    }

    return now - transcriptionJob.updated >= AUDIO_NOTE_TRANSCRIPTION_STALLED_MS;
};

/**
 * Returns the best progress number to display for a job. Completed and skipped
 * jobs default to 100% so older records still show a finished state even if
 * the backend omitted the final progress write.
 */
const getAudioNoteTranscriptionStatusProgress = (
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
): number | null => {
    if (!transcriptionJob) {
        return null;
    }

    if (
        transcriptionJob.status === AUDIO_TRANSCRIPTION_JOB_STATUS.completed
        || transcriptionJob.status === AUDIO_TRANSCRIPTION_JOB_STATUS.skipped
    ) {
        return 100;
    }

    const isPendingStatus = (
        transcriptionJob.status === AUDIO_TRANSCRIPTION_JOB_STATUS.queued
        || transcriptionJob.status === AUDIO_TRANSCRIPTION_JOB_STATUS.processing
        || transcriptionJob.status === AUDIO_TRANSCRIPTION_JOB_STATUS.offloaded
    );
    const phaseDisplayProgress = getAudioNoteExpectedAlignmentDisplayProgress(
        transcriptionJob,
    );
    if (isPendingStatus && phaseDisplayProgress != null) {
        if (
            typeof transcriptionJob.progress === 'number'
            && Number.isFinite(transcriptionJob.progress)
        ) {
            return Math.max(
                0,
                Math.min(100, Math.max(Math.round(transcriptionJob.progress), phaseDisplayProgress)),
            );
        }

        return phaseDisplayProgress;
    }

    return typeof transcriptionJob.progress === 'number'
        && Number.isFinite(transcriptionJob.progress)
        ? Math.max(0, Math.min(100, Math.round(transcriptionJob.progress)))
        : null;
};

const isSpeechGenerationJob = (
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
): boolean => (
    transcriptionJob?.jobKind === 'speech_generation'
    || transcriptionJob?.jobKind === 'speech_generation_with_alignment'
);

const isSpeechGenerationWithAlignmentJob = (
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
): boolean => transcriptionJob?.jobKind === 'speech_generation_with_alignment';

/**
 * Maps backend job states to concise UI text for the audio-note toolbar.
 */
export const getAudioNoteTranscriptionStatusLabel = (
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
    now = Date.now(),
): string | null => {
    if (!transcriptionJob) {
        return null;
    }

    const transcriptionProgress = getAudioNoteTranscriptionStatusProgress(transcriptionJob);
    const progressSuffix = transcriptionProgress != null
        ? ` ${transcriptionProgress}%`
        : '';
    const speechGenerationJob = isSpeechGenerationJob(transcriptionJob);
    const speechGenerationWithAlignmentJob = isSpeechGenerationWithAlignmentJob(transcriptionJob);
    if (isAudioNoteTranscriptionStatusStalled(transcriptionJob, now)) {
        if (speechGenerationWithAlignmentJob && (transcriptionProgress ?? 0) >= 40) {
            return `Transcript Timing Stalled${progressSuffix}`;
        }

        if (speechGenerationJob) {
            return `Speech Generation Stalled${progressSuffix}`;
        }

        return `Transcription Stalled${progressSuffix}`;
    }

    switch (transcriptionJob.status) {
    case AUDIO_TRANSCRIPTION_JOB_STATUS.queued:
        return `Queued${progressSuffix}`;
    case AUDIO_TRANSCRIPTION_JOB_STATUS.processing:
    case AUDIO_TRANSCRIPTION_JOB_STATUS.offloaded:
        if (speechGenerationWithAlignmentJob && (transcriptionProgress ?? 0) >= 40) {
            return `Timing Transcript...${progressSuffix}`;
        }

        if (speechGenerationJob) {
            return `Generating Speech...${progressSuffix}`;
        }

        return `Transcribing...${progressSuffix}`;
    case AUDIO_TRANSCRIPTION_JOB_STATUS.completed:
        if (speechGenerationWithAlignmentJob) {
            return `Speech + Transcript Ready${progressSuffix}`;
        }

        if (speechGenerationJob) {
            return `Speech Ready${progressSuffix}`;
        }

        return `Transcript Ready${progressSuffix}`;
    case AUDIO_TRANSCRIPTION_JOB_STATUS.failed:
        if (speechGenerationWithAlignmentJob) {
            return `Speech/Transcript Failed${progressSuffix}`;
        }

        if (speechGenerationJob) {
            return `Speech Generation Failed${progressSuffix}`;
        }

        return `Transcription Failed${progressSuffix}`;
    case AUDIO_TRANSCRIPTION_JOB_STATUS.skipped:
        if (speechGenerationJob) {
            return `Speech Skipped${progressSuffix}`;
        }

        return `Transcript Skipped${progressSuffix}`;
    default:
        return null;
    }
};

/**
 * Pending states show an inline spinner so long uploads have visible progress.
 */
export const isAudioNoteTranscriptionStatusPending = (
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
    now = Date.now(),
): boolean => {
    if (isAudioNoteTranscriptionStatusStalled(transcriptionJob, now)) {
        return false;
    }

    return (
        transcriptionJob?.status === AUDIO_TRANSCRIPTION_JOB_STATUS.queued
        || transcriptionJob?.status === AUDIO_TRANSCRIPTION_JOB_STATUS.processing
        || transcriptionJob?.status === AUDIO_TRANSCRIPTION_JOB_STATUS.offloaded
    );
};

export const isAudioNoteTranscriptionStatusFailure = (
    transcriptionJob: IEditorAudioNoteItem['transcriptionJob'] | null | undefined,
): boolean => transcriptionJob?.status === AUDIO_TRANSCRIPTION_JOB_STATUS.failed;
